"use client";

import { useState, useEffect } from "react";

import { useRouter } from "next/navigation";

import {
  CalculatorOutlined,
  FundProjectionScreenOutlined,
  StarOutlined,
  StockOutlined,
} from "@ant-design/icons";
import hotkeys from "hotkeys-js";
import Cookies from "js-cookie";

import { ROUTE, URLS } from "@/lib/constants";
import { getFingerprint } from "@/lib/fingerprint.js";
import { blueSearch, greySearch, logoSvg } from "@/lib/imageIcon";

import BrowserSearchbar from "./BrowserSearchbar";
import HeaderModal from "./modal/headermodal";

const Header = ({ setOpen }) => {
  const router = useRouter();
  const blueSearchImage = blueSearch;
  const greySearchImage = greySearch;
  const xToken = Cookies.get("x-token");
  const [isHover, setIsHover] = useState(false);
  const [searchToggle, _searchToggle] = useState(false);
  const [isMac, setIsMac] = useState(false);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [toggle, setToggle] = useState({
    calculator: false,
    portfolio: false,
    watchlist: false,
  });

  const navItems = [
    {
      icon: <StockOutlined />,
      label: "Stocks",
      onClick: () => {
        setOpen && setOpen(false);
        router.push(ROUTE?.HOME);
      },
    },
    {
      icon: <CalculatorOutlined />,
      label: "Retirement Calculator",
      onClick: () => {
        setOpenModal(true);
        setToggle({ ...toggle, calculator: !toggle.calculator });
      },
    },
    {
      icon: <FundProjectionScreenOutlined />,
      label: "Portfolio Tracker",
      onClick: () => {
        setOpenModal(true);
        setToggle({ ...toggle, portfolio: !toggle.portfolio });
      },
    },
    {
      icon: <StarOutlined />,
      label: "Watchlist",
      onClick: () => {
        setOpenModal(true);
        setToggle({ ...toggle, watchlist: !toggle.watchlist });
      },
    },
  ];

  useEffect(() => {
    const fetchFingerprint = async () => {
      const id = await getFingerprint();
      localStorage.setItem("id", id);
      Cookies.set("id", id);
    };
    fetchFingerprint();
  }, []);

  useEffect(() => {
    setIsMac(navigator.platform.indexOf("Mac") > -1);
  }, []);

  useEffect(() => {
    const handleHotkey = (e) => {
      e.preventDefault();
      _searchToggle(!searchToggle);
    };

    if (isMac) {
      hotkeys("cmd+k", handleHotkey);
    } else {
      hotkeys("ctrl+k", handleHotkey);
    }

    // Cleanup function
    return () => {
      hotkeys.unbind("cmd+k");
      hotkeys.unbind("ctrl+k");
    };
  }, [isMac, _searchToggle, searchToggle]);

  return (
    <header className="header-div">
      {openModal && (
        <HeaderModal
          toggle={toggle}
          setToggle={setToggle}
          setShowModal={setOpenModal}
          showModal={openModal}
        />
      )}
      {searchToggle && (
        <div
          className="advance-search-bg-blue p-0"
          style={{ height: document.documentElement.scrollHeight }}
        ></div>
      )}
      {searchToggle && (
        <BrowserSearchbar
          _searchToggle={_searchToggle}
          searchToggle={searchToggle}
          setPage={setPage}
          page={page}
        />
      )}
      <div className="d-flex justify-content-between header-logo_search">
        <div className="d-flex justify-content-evenly align-items-center w-100">
          <img
            src={logoSvg}
            alt="TYKR Logo"
            width={110}
            height={60}
            className="cursor-pointer mr-48"
            onClick={() => {
              setOpen && setOpen(false);
              router.push(URLS?.TYKR);
            }}
          />
          <div
            className="d-flex header-search-div header-search-div-mobile justify-content-between cursor-pointer mr-48"
            onClick={() => {
              _searchToggle(true);
            }}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
          >
            <div className="d-flex m-0 align-items-center">
              <img
                alt="Search"
                src={isHover ? blueSearchImage : greySearchImage}
                width={16}
                height={16}
              />
              <div>
                <p className="mb-0 search-text"> Search...</p>
              </div>
            </div>
            <div className="search-command-div">
              <p className="mb-0 search-command">
                {isMac ? "CMD" : "CTRL"} + K
              </p>
            </div>
          </div>
          <div className="d-flex justify-content-between navItems align-items-center gap-6">
            {navItems.map((item, index) => (
              <div
                key={index}
                className="align-items-center navigate-link d-flex cursor-pointer"
                onClick={() => {
                  item.onClick();
                }}
              >
                <span>{item.icon}</span>
                <span className="ml-10">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="search-div">
          <div className="d-flex justify-content-end gap-4">
            {xToken ? (
              <div
                onClick={() => {
                  window.open("https://app.tykr.com/", "_blank");
                }}
              >
                <p className="mb-0 green-search-command cursor-pointer">
                  Go to Tykr platform
                </p>
              </div>
            ) : (
              <>
                <div className="login-header">
                  <p
                    className="mb-0 search-command1 cursor-pointer"
                    onClick={() => {
                      window.open(URLS.TYKR_LOGIN, "_blank");
                    }}
                  >
                    Log in
                  </p>
                </div>
                <div
                  onClick={() => {
                    window.open(URLS.TYKR_SIGNUP, "_blank");
                  }}
                >
                  <p className="mb-0 green-search-command cursor-pointer">
                    I want my free trial
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="d-flex mobile-search-div gap-4">
          <img
            alt="Search"
            width={20}
            height={20}
            className="cursor-pointer"
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            src={isHover ? blueSearchImage : greySearchImage}
            onClick={() => {
              _searchToggle(true);
            }}
          />
          {xToken ? (
            <div
              onClick={() => {
                window.open("https://app.tykr.com/", "_blank");
              }}
            >
              <p
                className="mb-0 green-search-command cursor-pointer"
                style={{ width: 160 }}
              >
                Go to Tykr platform
              </p>
            </div>
          ) : (
            <>
              <div className="login-header">
                <p
                  className="mb-0 search-command1 cursor-pointer"
                  onClick={() => {
                    window.open(URLS.TYKR_LOGIN, "_blank");
                  }}
                >
                  Log in
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
